// src/services/userServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/users`;

// Lit le token à chaque appel (utile si l’utilisateur se reconnecte)
const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/* =========================
   AUTHENTIFICATION
   ========================= */

// Connexion (email + password) → token + userInfo
export const loginUser = async (email, password) => {
  try {
    const response = await axios.post(`${apiUrl}/login`, { email, password });
    return response.data; // { message, token, userInfo }
  } catch (error) {
    console.error("Erreur lors de la connexion de l'utilisateur :", error);
    return null; // on garde le comportement précédent
  }
};

// Inscription (utilisée surtout via invitation, pas de /register public)
export const registerUser = async (nom, prenom, email, password) => {
  try {
    const response = await axios.post(`${apiUrl}/register`, {
      nom,
      prenom,
      email,
      password,
    });
    // On garde la forme { data } / { error } que tu avais déjà
    return { data: response.data };
  } catch (error) {
    console.error("Erreur lors de l'inscription de l'utilisateur :", error);
    let errorMessage = "Erreur lors de l'inscription. Veuillez réessayer.";
    if (
      error.response &&
      error.response.data &&
      typeof error.response.data.message === "string"
    ) {
      errorMessage = error.response.data.message;
    } else if (error.response && error.response.data.errors) {
      errorMessage = error.response.data.errors
        .map((err) => err.message)
        .join(", ");
    }
    return { error: errorMessage };
  }
};

// Validation du token côté API générale (/api/validateToken)
export const validateToken = async () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  try {
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/validateToken`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data; // { id, email, nom, prenom, photoURL, role_id }
  } catch (error) {
    console.error("Erreur lors de la validation du token :", error);
    return null;
  }
};

/* =========================
   UTILISATEURS (ADMIN)
   ========================= */

// Liste des utilisateurs (pour la page Admin > Utilisateurs)
export const getAllUsers = async () => {
  try {
    const response = await axios.get(`${apiUrl}/allusers`, {
      headers: authHeaders(),
    });

    if (!response.data || !Array.isArray(response.data)) {
      throw new Error("Format de données invalide : tableau attendu");
    }
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement des utilisateurs :", error);
    return []; // éviter les crashs côté front
  }
};

// Détail d’un utilisateur
export const getUserById = async (userId) => {
  try {
    const response = await axios.get(`${apiUrl}/oneUser/${userId}`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors du chargement de l'utilisateur :", error);
    return null;
  }
};

// Création d’un utilisateur par l’admin (depuis la page Users Admin)
export const addUserByAdmin = async (userData) => {
  try {
    const config = {
      headers: {
        "Content-Type": "application/json",
        ...authHeaders(),
      },
    };

    const response = await axios.post(
      `${apiUrl}/addUserByAdmin`,
      userData,
      config
    );

    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la création de l'utilisateur:",
      error.response ? error.response.data : error
    );
    console.log("Données envoyées:", userData);
    return null;
  }
};

// Mise à jour complète d’un utilisateur (nom, prénom, email, rôle, photoURL…)
export const updateUser = async (userId, userData) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${userId}/updateUser`,
      userData,
      {
        headers: authHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de l'utilisateur :", error);
    throw error;
  }
};

// Mise à jour du profil par l’utilisateur lui-même (nom, prénom, photoURL…)
export const updateUserProfile = async (userId, userData) => {
  try {
    const response = await axios.patch(
      `${apiUrl}/${userId}/updateUserProfile`,
      userData,
      {
        headers: authHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la mise à jour des données de l'utilisateur :",
      error
    );
    throw error;
  }
};

// Changer le rôle (1 = Admin, 2 = Utilisateur)
export const updateUserRole = async (userId, roleId) => {
  try {
    const response = await axios.put(
      `${apiUrl}/${userId}/updateRole`,
      { role_id: roleId },
      {
        headers: authHeaders(),
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du rôle :", error);
    throw error;
  }
};

// Supprimer un utilisateur
export const deleteUser = async (userId) => {
  try {
    const response = await axios.delete(`${apiUrl}/${userId}/deleteUser`, {
      headers: authHeaders(),
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression de l'utilisateur :", error);
    return null;
  }
};

/* =========================
   STATS / DASHBOARD
   ========================= */

// Compter les utilisateurs (utilisé dans le tableau de bord Admin)
export const countUsers = async () => {
  try {
    const response = await axios.get(`${apiUrl}/countUsers`, {
      headers: authHeaders(),
    });
    // Pour MonAppliMairie tu pourras renvoyer côté back :
    // { total, admins, users } par ex.
    return response.data;
  } catch (error) {
    console.error("Erreur lors du comptage des utilisateurs :", error);
    return 0;
  }
};

// Vérifier si un email est déjà utilisé
export const checkEmailExists = async (email) => {
  try {
    const response = await axios.get(`${apiUrl}/checkEmail`, {
      params: { email },
    });
    return { exists: response.data.exists };
  } catch (error) {
    console.error("Erreur lors de la vérification de l'email :", error);
    return {
      exists: false,
      error:
        error.response?.data?.message ||
        "Erreur lors de la vérification de l'email",
    };
  }
};

/* =========================
   FORMULAIRE DE CONTACT DU SITE
   ========================= */

export const sendContactForm = async (formData) => {
  try {
    await axios.post(`${apiUrl}/send-contact-form`, formData);
  } catch (error) {
    console.error("Erreur lors de l'envoi du message :", error);
  }
};
